const throwIfEmpty = (roles) => {
    if (!roles.length) {
        throw new Error('At least one role is required');
    }
};
/**
 * Helper function to evaluate a role input (single role or predicate)
 * @param roleInput - The role input to evaluate
 * @param userRoles - The user's current roles
 * @returns True if the role input matches the user's roles
 */
export const evaluateRoleInput = (roleInput, userRoles) => {
    // If it's a function (predicate), call it
    if (typeof roleInput === 'function') {
        return roleInput(userRoles);
    }
    // If it's a single role, check if user has this role
    return userRoles.includes(roleInput);
};
/**
 * Creates a predicate that checks if the user has ALL of the specified roles (AND condition)
 * @param roles - Variable number of roles or nested predicates
 * @returns A predicate function that returns true if user has all specified roles
 * @example and('ADMIN', 'USER') or and(or('ADMIN', 'MANAGER'), 'ACTIVE')
 */
export const and = (...roles) => {
    throwIfEmpty(roles);
    return (userRoles) => roles.every((roleInput) => evaluateRoleInput(roleInput, userRoles));
};
/**
 * Creates a predicate that checks if the user has ANY of the specified roles (OR condition)
 * @param roles - Variable number of roles or nested predicates
 * @returns A predicate function that returns true if user has any of the specified roles
 * @example or('ADMIN', 'USER') or or(and('ADMIN', 'ACTIVE'), 'SUPER_USER')
 */
export const or = (...roles) => {
    throwIfEmpty(roles);
    return (userRoles) => roles.some((roleInput) => evaluateRoleInput(roleInput, userRoles));
};
/**
 * Creates a predicate that checks if the user does NOT have the specified role/condition (NOT condition)
 * @param roleInput - Single role or nested predicate
 * @returns A predicate function that returns true if user does not have the specified role/condition
 * @example not('ADMIN') or not(and('ADMIN', 'ACTIVE'))
 */
export const not = (roleInput) => (userRoles) => !evaluateRoleInput(roleInput, userRoles);
